import { useAtomValue } from 'jotai'
import React, { ReactNode } from 'react'

import { isSolanaChain } from '@cowprotocol/cow-sdk'
import { useWalletDetails, useWalletInfo } from '@cowprotocol/wallet'

import { ordersToCancelSetAtom } from 'entities/ordersToCancel/ordersToCancel.atom'
import { OrderTabId } from 'entities/routes/routes.atom'

import { useGetSpotPrice, usePendingOrdersPrices } from 'modules/orders'
import { useOrderActions } from 'modules/ordersTable/hooks/useOrderActions'
import { ordersTableStateAtom } from 'modules/ordersTable/state/ordersTable.atoms'

import { OrdersTableRowGroup } from './Group/OrdersTableRowGroup.pure'

import { OrderRow } from '../../../containers/OrderRow/OrderRow.container'
import { OrderTableItem } from '../../../state/ordersTable.types'
import { useGetPendingOrdersPermitValidityState } from '../../../state/permit/usePendingOrderPermitValidity'
import { getOrderParams } from '../../../utils/getOrderParams'
import { getParsedOrderFromTableItem, isParsedOrder } from '../../../utils/orderTableGroupUtils'

interface OrderTableRowProps {
  currentTab: OrderTabId
  isTwapTable: boolean
  item: OrderTableItem
}

export function OrdersTableRow({ currentTab, isTwapTable, item }: OrderTableRowProps): ReactNode {
  const { chainId } = useWalletInfo()
  const { allowsOffchainSigning } = useWalletDetails()
  const ordersTableState = useAtomValue(ordersTableStateAtom)
  const pendingOrdersPermitValidityState = useGetPendingOrdersPermitValidityState()
  const getSpotPrice = useGetSpotPrice()
  const pendingOrdersPrices = usePendingOrdersPrices()
  const ordersToCancelSet = useAtomValue(ordersToCancelSetAtom)
  const orderActions = useOrderActions()

  const { balancesAndAllowances } = ordersTableState

  // Solana has no off-chain (EIP-712) signing concept, so `allowsOffchainSigning` never applies there -
  // its batch cancellation is a bundled on-chain transaction instead, gated only on being on a Solana chain.
  const isRowSelectable = (allowsOffchainSigning || isSolanaChain(chainId)) && !isTwapTable

  const { inputToken, outputToken } = getParsedOrderFromTableItem(item)

  const spotPrice = getSpotPrice({
    chainId,
    sellTokenAddress: inputToken.address,
    buyTokenAddress: outputToken.address,
  })

  if (isParsedOrder(item)) {
    const order = item

    return (
      <OrderRow
        isRowSelectable={isRowSelectable}
        isRowSelected={ordersToCancelSet.has(order.id)}
        isHistoryTab={currentTab === OrderTabId.HISTORY}
        order={order}
        spotPrice={spotPrice}
        prices={pendingOrdersPrices[order.id]}
        isRateInverted={false}
        orderParams={getOrderParams(chainId, balancesAndAllowances, order, pendingOrdersPermitValidityState)}
        onClick={() => orderActions.selectReceiptOrder(order)}
        orderActions={orderActions}
        isTwapTable={isTwapTable}
        chainId={chainId}
        balancesAndAllowances={balancesAndAllowances}
      />
    )
  } else {
    return (
      <OrdersTableRowGroup
        item={item}
        chainId={chainId}
        balancesAndAllowances={balancesAndAllowances}
        isRowSelectable={isRowSelectable}
        isRowSelected={ordersToCancelSet.has(item.parent.id)}
        isHistoryTab={currentTab === OrderTabId.HISTORY}
        spotPrice={spotPrice}
        prices={pendingOrdersPrices[item.parent.id]}
        isRateInverted={false}
        orderActions={orderActions}
        isTwapTable={isTwapTable}
      />
    )
  }
}
